// frontend/src/components/ExpenseCreateModal.jsx
// Project:      Vision Systems / Vision ANPR
// Author:       Castle Traffic Ltd
// Created:      2026-11-02 GMT
// Updated:      2026-11-02 GMT — EXP-03 shared independent Expense creation
// Purpose:      Independent desktop/mobile Expense modal backed by EXP-02 authorization.
// Operational:  No client-supplied tenant_id; claimant defaults server-side. Expense type
//               metadata is selected for UI classification; current EXP-02 ExpenseCreate
//               schema has no expense_type_id, so it is NOT falsely persisted.
//               Existing deferred Job Expense accordion remains unchanged.
// 2026-11-02 GMT: Job context prefill, reset on launch and Job Edit overlay stacking.

import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../api/axiosInstance';

const initial = { resource_id: '', customer_id: '', job_id: '', category: '', expense_type: '',
  amount: '', currency: 'GBP', occurred_at: new Date().toISOString().slice(0, 10),
  description: '', company_paid: false, billable_to_customer: true };
const field = { display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0, fontWeight: 600, fontSize: 13 };
const input = { width: '100%', minWidth: 0, padding: '10px 11px', border: '1px solid var(--border-color, #a4a4a4)',
  borderRadius: 7, background: 'var(--widget-background, var(--card-bg, #fff))', color: 'inherit', boxSizing: 'border-box', font: 'inherit' };
const columns = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 235px), 1fr))', gap: 14 };
const button = (kind) => ({ padding: '10px 18px', border: 0, borderRadius: 7, color: '#fff', cursor: 'pointer',
  fontWeight: 700, background: `var(--btn-${kind}-normal, ${kind === 'action' ? '#18778a' : '#333'})` });
const apiError = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return Array.isArray(detail) ? detail.map((item) => item.msg || JSON.stringify(item)).join('; ') : err.message || 'Request failed';
};

export default function ExpenseCreateModal({ open, onClose, onCreated, context = null }) {
  const [data, setData] = useState(initial);
  const [catalog, setCatalog] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [resources, setResources] = useState([]);
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [delegation, setDelegation] = useState(false);
  const update = (key, value) => setData((prev) => ({ ...prev, [key]: value,
    ...(key === 'customer_id' ? { job_id: '' } : {}),
    ...(key === 'category' ? { expense_type: '' } : {}) }));

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setError('');
    setData({ ...initial, occurred_at: new Date().toISOString().slice(0, 10),
      customer_id: context?.customerId ? String(context.customerId) : '',
      job_id: context?.jobId ? String(context.jobId) : '' });
    setReceipt(null);
    setLoading(true);
    Promise.allSettled([
      axiosInstance.get('/expenses/catalog'),
      axiosInstance.get('/customers/', { params: { limit: 1000 } }),
      axiosInstance.get('/jobs/'),
      axiosInstance.get('/resources/', { params: { limit: 1000 } }),
      axiosInstance.get('/users/me'),
    ]).then((results) => {
      if (!active) return;
      const [c, cu, j, r, me] = results;
      if (c.status === 'fulfilled') setCatalog(Array.isArray(c.value.data) ? c.value.data : []);
      else setError('Unable to load Expense catalogue: ' + apiError(c.reason));
      if (cu.status === 'fulfilled') setCustomers(Array.isArray(cu.value.data) ? cu.value.data : []);
      if (j.status === 'fulfilled') setJobs(Array.isArray(j.value.data) ? j.value.data : []);
      if (r.status === 'fulfilled') setResources(Array.isArray(r.value.data) ? r.value.data : []);
      // Delegation is an optional UI convenience, never the security boundary.
      // EXP-02 remains responsible for refusing an unauthorised resource_id.
      if (me.status === 'fulfilled') setDelegation([1, 3].includes(Number(me.value.data?.role_id)));
      else setDelegation(false);
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, context]);

  useEffect(() => {
    if (!open) return undefined;
    const keyHandler = (event) => { if (event.key === 'Escape' && !busy) onClose?.(); };
    window.addEventListener('keydown', keyHandler);
    return () => window.removeEventListener('keydown', keyHandler);
  }, [open, busy, onClose]);

  const selectedCategory = useMemo(() => catalog.find((c) => c.name === data.category), [catalog, data.category]);
  const filteredJobs = useMemo(() => jobs.filter((job) => !data.customer_id || Number(job.customer_id) === Number(data.customer_id)), [jobs, data.customer_id]);
  const save = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!data.category || !data.amount || !data.occurred_at) { setError('Date, amount and category are required.'); return; }
    if (data.job_id && !data.customer_id) { setError('Select the Customer for the chosen Job.'); return; }
    setError(''); setBusy(true);
    try {
      const payload = { amount: Number(data.amount), currency: data.currency, category: data.category,
        description: data.description.trim() || null,
        occurred_at: new Date(`${data.occurred_at}T12:00:00`).toISOString(),
        company_paid: Boolean(data.company_paid), billable_to_customer: Boolean(data.billable_to_customer),
        customer_id: data.customer_id ? Number(data.customer_id) : null,
        job_id: data.job_id ? Number(data.job_id) : null };
      if (delegation && data.resource_id) payload.resource_id = Number(data.resource_id);
      const created = await axiosInstance.post('/expenses', payload);
      if (receipt) {
        const fd = new FormData(); fd.append('file', receipt, receipt.name);
        try { await axiosInstance.post(`/expenses/${created.data.id}/receipt`, fd); }
        catch (uploadError) {
          setError(`Expense #${created.data.id} was created but receipt upload failed: ${apiError(uploadError)}. Reopen the expense to attach it; do not submit again.`);
          onCreated?.(created.data);
          return;
        }
      }
      onCreated?.(created.data);
      onClose?.();
    } catch (err) { setError(apiError(err)); }
    finally { setBusy(false); }
  };
  if (!open) return null;
  return (
    <div role="presentation" style={{ position: 'fixed', inset: 0, zIndex: 200000, background: 'rgba(0,0,0,.66)',
      display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 12, boxSizing: 'border-box' }}>
      <style>{`.vision-expense-btn-general:hover:not(:disabled){background:var(--btn-general-hover,var(--btn-general-normal,#333))!important}
        .vision-expense-btn-general:active:not(:disabled){background:var(--btn-general-click,var(--btn-general-normal,#333))!important}
        .vision-expense-btn-action:hover:not(:disabled){background:var(--btn-action-hover,var(--btn-action-normal,#18778a))!important}
        .vision-expense-btn-action:active:not(:disabled){background:var(--btn-action-click,var(--btn-action-normal,#18778a))!important}`}</style>
      <section role="dialog" aria-modal="true" aria-labelledby="vision-new-expense-title" style={{ width: 'min(780px,100%)', maxHeight: 'min(92dvh,900px)', overflowY: 'auto',
        background: 'var(--widget-background,var(--card-bg,#fff))', color: 'var(--text-color,inherit)', borderRadius: 12, padding: 22,
        boxShadow: '0 20px 70px #0007', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <h2 id="vision-new-expense-title" style={{ margin: '0 0 18px' }}>Add Expense</h2>
          <button type="button" aria-label="Close" disabled={busy} onClick={onClose} style={{ ...button('general'), padding: '6px 12px' }}>×</button>
        </div>
        <form onSubmit={save}>
          <div style={columns}>
            <label style={field}>Expense date<input style={input} required type="date" value={data.occurred_at} onChange={(e) => update('occurred_at', e.target.value)} /></label>
            <label style={field}>Amount<input style={input} required type="number" min="0.01" step="0.01" value={data.amount} onChange={(e) => update('amount', e.target.value)} /></label>
            <label style={field}>Currency<select style={input} value={data.currency} onChange={(e) => update('currency', e.target.value)}><option value="GBP">GBP</option><option value="EUR">EUR</option></select></label>
            <label style={field}>Category<select style={input} required value={data.category} onChange={(e) => update('category', e.target.value)}><option value="">Select category</option>{catalog.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}</select></label>
            <label style={field}>Expense type (classification only)<select style={input} value={data.expense_type} onChange={(e) => update('expense_type', e.target.value)} disabled={!selectedCategory}><option value="">Select type (optional)</option>{(selectedCategory?.expense_types || []).filter((t) => t.financial_origin === 'employee_expense' || t.financial_origin === 'dual').map((t) => <option key={t.code} value={t.code}>{t.name}</option>)}</select></label>
            {delegation && <label style={field}>Claimant (administrator only)<select style={input} value={data.resource_id} onChange={(e) => update('resource_id', e.target.value)}><option value="">My linked Resource (default)</option>{resources.map((r) => <option key={r.id} value={r.id}>{[r.first_name,r.last_name].filter(Boolean).join(' ') || r.identifier || `Resource ${r.id}`}</option>)}</select></label>}
            <label style={field}>Customer (optional)<select style={input} value={data.customer_id} onChange={(e) => update('customer_id', e.target.value)}><option value="">Not linked to Customer</option>{customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
            <label style={field}>Job (optional)<select style={input} value={data.job_id} onChange={(e) => update('job_id', e.target.value)} disabled={!data.customer_id}><option value="">Not linked to Job</option>{filteredJobs.map((j) => <option key={j.id} value={j.id}>{j.job_ref || `Job ${j.id}`} — {j.status || 'Unknown'}</option>)}</select></label>
          </div>
          <label style={{ ...field, marginTop: 14 }}>Description<textarea style={{ ...input, resize: 'vertical' }} rows={3} value={data.description} onChange={(e) => update('description', e.target.value)} /></label>
          <div style={{ ...columns, marginTop: 14 }}>
            <label style={{ ...field, flexDirection: 'row', alignItems: 'center' }}><input type="checkbox" checked={data.company_paid} onChange={(e) => update('company_paid', e.target.checked)} /> Company-paid (employee reimbursement £0)</label>
            <label style={{ ...field, flexDirection: 'row', alignItems: 'center' }}><input type="checkbox" checked={data.billable_to_customer} onChange={(e) => update('billable_to_customer', e.target.checked)} /> Billable to Customer</label>
          </div>
          <label style={{ ...field, marginTop: 14 }}>Receipt (optional)<input style={input} type="file" accept="image/*,.pdf" onChange={(e) => setReceipt(e.target.files?.[0] || null)} /></label>
          <p style={{ fontSize: 12, opacity: .75 }}>Created as draft. Submit and approval remain separate operations. Expense type is not yet stored on the Expense record.</p>
          {error && <div role="alert" style={{ padding: 10, margin: '12px 0', border: '1px solid #b44', borderRadius: 6, overflowWrap: 'anywhere' }}>{error}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
            <button className="vision-expense-btn-general" type="button" disabled={busy} onClick={onClose} style={button('general')}>Cancel</button>
            <button className="vision-expense-btn-action" type="submit" disabled={busy || loading || !catalog.length} style={button('action')}>{busy ? 'Saving…' : 'Save Expense'}</button>
          </div>
        </form>
      </section>
    </div>
  );
}
